import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UserDataService } from '../../../shared/services/userData.service';
import { HazardAnnexesService } from '../../../shared/services/hazardAnnexes.service';
import { IAssessment } from '../../../shared/models/assessment';
import { Icons } from '../../../shared/helpers/icons';
import { SpinnerComponent } from "../../components/spinner/spinner.component";
import { EAssessmentSubTypes, EAssessmentTypes } from '../../../shared/models/eop_types.enum';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { IUserInfo } from '../../../shared/models/contact';

@Component({
  selector: 'edit-hazard-assessment',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    SpinnerComponent
  ],
  templateUrl: './edit-hazard-assessment.component.html',
  styleUrl: './edit-hazard-assessment.component.css'
})
export class EditHazardAssessmentComponent implements OnInit, OnChanges {

  @Input() assessmentToEdit: IAssessment | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  currentAssessmentSection: string = '';
  currentAssessmentSubSection: string = '';

  formData: Record<string, boolean | string> = {};
  comments: string = '';

  formSectionFields: Record<string, string[]> = {
    'AL-School Building': [
      'Long, unsupported roof spans',
      'Large, window panes (especially over exits)',
      'Heating and air conditioning units',
      'Overhangs',
      'Exit doors improperly marked',
      'Exit doors not opening to the outside',
      'Missing or malfunctioning panic hardware on exit doors',
      'Broken or nonfunctioning surveillance cameras and monitoring equipment',
      'Safety zones improperly marked in career technical shop',
      'Safety signs missing or illegible in career technical shop',
      'Missing, improper, or damaged safety guards on machines in career technical building or shops/work areas',
      'Safety eyewear available for all students operating career technical machinery or performing experiments in the science lab',
      'Outside fire escapes present for buildings with two or more stories (for buildings without fireproof stairs)',
      'Fire escape directional diagrams posted in all rooms that house students',
      'Unsecured fire extinguishers or fire extinguishers not properly charged and tagged',
      'Safe-place diagrams posted for severe weather in all rooms that house students',
      'Toxic, corrosive, or flammable materials not stored properly',
      'Inadequate ventilation systems in chemical storage areas',
      'Malfunctioning alarm system',
      'Hazardous materials located in areas without warning signs',
      'Unsecured appliances',
      'Unsecured filing cabinets or cabinets with inadequate drawer latches',
      'Inadequately supported light fixtures',
      'Unanchored table lamps',
      'Unsecured athletic equipment',
      'Other'
    ],
    'AL-Playground': [
      'Equipment in need of repair',
      'Rocks or other material that could cause injury',
      'Fences in need of repair',
      'Exposed nails, screws, or bolts',
      'Traffic pattern during the school day',
      'Schedule playground safety inspections on a regular basis',
      'Other'
    ],
    'AL-School Grounds': [
      'Trees or shrubs that present fire or wind hazards or provide areas for intruders to hide',
      'Streams in close proximity',
      'Electric wires',
      'Gasoline or propane tanks',
      'Natural gas lines',
      'Fences in need of repair',
      'Traffic pattern during the school day',
      'Insufficient lighting for activities in all areas of the campus',
      'Other'
    ],
    'AL-Other Structures': [
      'Unsecured portable structures',
      'Portable buildings properly anchored',
      'Unsecured siding or roofing materials',
      'Incompatible chemical storage',
      'Inadequate ventilation',
      'Other fire hazards (List)',
      'Other'
    ],
    'AL-Classroom': [
      'Free-standing cabinets, bookcases, and wall shelves',
      'Heavy objects on high shelves',
      'Aquariums and other potentially hazardous displays located near seating areas',
      'Unsecured TV monitors',
      'Unsecured wall-mounted objects',
      'Hanging plants above or near seating areas',
      'Incompatible chemicals stored in close proximity (e.g., window cleaner and ammonia)',
      'Paper or other combustibles (e.g., greasy rags) stored near heat source',
      'No safety glasses or shields in science/chemistry labs and vocational classrooms',
      'Floors not covered with nonskid wax or nonskid pads at each water-bearing workstation',
      'Lack of ground fault interrupters (GFI) within arms reach of faucets',
      'Inadequate or no fire blankets (non-asbestos) prominently labeled and strategically located in the lab',
      'Lack of or poorly functioning eyewash stations in the lab or vocational classroom',
      'Lack of or poorly functioning master shutoffs for gas and electric in the lab or vocational classroom',
      'Other'
    ],
    'AL-Evacuation Routes': [
      'Hallways and/or doors containing glass panels that are other than tempered glass or Plexiglas',
      'Lockers, bookshelves, or other storage units along hallways + Hallways may be cluttered with debris from ceilings, fallen light fixtures, broken glass, and toppled storage units. Students should be advised to anticipate these hazard',
      'Lighting that is dependent on electricity rather than sunlight (no emergency lighting)',
      'Elevators + Elevators are vulnerable to damage from fires, earthquakes, and other hazards. Signs should be posted near elevators prohibiting their use during emergencies',
      'Building exit routes that pass through canopies or porch-like structures',
      'Roofs with clay or slate tiles',
      'Building facings that include parapets, balconies, or cornices',
      'Gas, sewer, or power lines near the outdoor assembly area',
      'Other'
    ],
    'AL-Neighborhoods': [
      'Facilities containing toxic, chemically reactive, and/or radioactive materials. * Be sure to include both manufacturers and users (e.g., gas stations, storage facilities, pesticide manufacturers/distributors, nuclear power plants)',
      'High-voltage power lines and substations',
      'Transportation routes of vehicles carrying hazardous materials (e.g., truck routes and railroad rights of way)',
      'Underground utility vaults and above-the-ground transformers',
      'Multi-story buildings vulnerable to damage or collapse (e.g., unreinforced masonry construction)',
      'Water towers, tanks, and treatment centers',
      'Gas storage facilities',
      'Natural gas plants',
      'Laboratory and Agricultural Facilities',
      'Fireworks Factories/Storage Facilities',
      'Military Instillations/Munitions Factories/Depots',
      'Pharmaceutical Companies',
      'Government buildings and landmarks',
      'High crime rate areas (e.g., drugs, prostitution, theft)',
      'Other'
    ]
  };
  
  loading: boolean = false;

  constructor(
    private userDataService: UserDataService,
    private hazardAnnexesService: HazardAnnexesService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    console.log(this.assessmentToEdit)
    this.initializeFormData();
  }

  // react to changes in the input
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assessmentToEdit'] && this.assessmentToEdit) {
      this.initializeFormData();
    }
  }

  private initializeFormData(): void {
    this.currentAssessmentSection = `AL-${this.assessmentToEdit?.type_name}`;
    this.currentAssessmentSubSection = `AL-${this.assessmentToEdit?.sub_type_name}`;

    const fields = this.formSectionFields[this.currentAssessmentSubSection] || [];
    this.formData = {}; 
    fields.forEach(field => {
      const existingValue = this.assessmentToEdit?.response_data?.[field];
      this.formData[field] = existingValue === 'Yes';
    });

    this.formData['comments'] = this.assessmentToEdit?.response_data?.['comments'] || '';
  }

  closeModal(): void {
    this.onClose.emit();
  }

  saveEdit(): void {
    if (this.currentBuildingId && this.assessmentToEdit) {
      const result: Record<string, string> = {};
  
      for (const key in this.formData) {
        const value = this.formData[key];
        if (key === 'comments' && value) result[key] = String(value);
        else if (value === true) result[key] = 'Yes';
      }
  
      if (this.currentAssessmentSection === 'AL-Classroom' && this.assessmentToEdit.response_data["room_number"].trim()) {
        result['room_number'] = this.assessmentToEdit?.response_data["room_number"].trim();
      }
      const payload = { 
        "contact_id": this.assessmentToEdit.contact_id,
        "receiver": this.assessmentToEdit.receiver,
        "response_data": result 
      };

      if (this.currentAssessmentSection === 'AL-Classroom') {
        this.hazardAnnexesService.updateALClassroomAssessment(this.currentBuildingId, String(this.assessmentToEdit.id), payload).subscribe({
          next: (res) => {
            this.loading = false;
            if (!res.has_error) {
              this.onSave.emit({ type: 'success', message: `This occurred after updating a ${(this.currentAssessmentSection || '').replace('AL-', '')} Assessment..` });
              this.closeModal();
            }
          },
          error: (err) => {
            this.loading = false;
            this.onSave.emit({ type: 'unsuccess', message: err.message });
          }
        })
      } else {
        this.hazardAnnexesService.updateALAssessment(this.currentBuildingId, String(EAssessmentTypes[this.currentAssessmentSection as keyof typeof EAssessmentTypes]), String(EAssessmentSubTypes[this.currentAssessmentSubSection as keyof typeof EAssessmentSubTypes]), payload).subscribe({
          next: (response) => {
            this.loading = false;
            if (!response.has_error) {
              this.onSave.emit({ type: 'success', message: `This occurred after updating ${(this.currentAssessmentSection || '').replace('AL-', '')} Assessment.`, data: response.data });
            }
          },
          error: (error: any) => {
            this.loading = false;
            this.onSave.emit({ type: 'unsuccess', message: error.message });
          }
        })
      }
    }
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }
}
